import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { Instagram } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { verifyInstagramConnection, autoLoginClaimedAccount } from '../services/instagramShowcaseService';

interface OAuthLocationState {
  authUrl?: string;
  returnPath?: string;
}

interface ConnectionData {
  redirectTo?: string;
  canSkipClaimStep?: boolean;
}

/**
 * OAuth Redirect Fallback Page
 *
 * This page is displayed when popup blockers prevent the OAuth popup from opening.
 * It provides a manual button for users to open the OAuth flow, which bypasses
 * popup blockers because it's triggered by a direct user interaction.
 *
 * After OAuth completes, it monitors localStorage for the token and redirects
 * to the appropriate page (dashboard for claimed accounts, onboarding for new).
 */
const OAuthRedirectPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Get authUrl and returnPath from navigation state
  const { authUrl, returnPath } = (location.state as OAuthLocationState) || {};

  useEffect(() => {
    if (!authUrl) {
      setError(t('oauth.noUrlError', 'No OAuth URL provided. Please try again.'));
    }

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [authUrl, t]);

  const handleOAuthSuccess = async (token: string) => {
    console.log('OAuth completed successfully, processing token...');
    localStorage.setItem('instagram_onboarding_token', token);

    try {
      // Check backend for redirect instruction
      const connectionData: ConnectionData = await verifyInstagramConnection(token);
      const redirectTo = connectionData.redirectTo || '/instagram-onboarding';
      console.log('Backend redirect instruction:', redirectTo);

      // If dashboard redirect, auto-login first (claimed account)
      if (redirectTo === '/dashboard' && connectionData.canSkipClaimStep) {
        console.log('Auto-login for returning user...');

        const loginData = await autoLoginClaimedAccount(token);

        if (loginData.success) {
          // Clear God Mode session state before new authentication
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonated_user');
          sessionStorage.removeItem('godmode_original_user');
          sessionStorage.removeItem('godmode_original_token');

          localStorage.setItem('token', loginData.token!);
          localStorage.setItem('user', JSON.stringify(loginData.user));
          toast.success(t('instagramShowcase.signIn.welcomeBack', 'Welcome back! Signed in with Instagram.'));
          setIsWaiting(false);
          navigate('/dashboard', { replace: true, state: { fromInstagramSignIn: true } });
          return;
        }
      }

      // Otherwise navigate to backend's redirect instruction (onboarding for new/unclaimed)
      setIsWaiting(false);
      toast.success(t('instagramShowcase.connect.connected', 'Instagram connected successfully!'));
      navigate(redirectTo, { replace: true });

    } catch (error) {
      console.error('Error checking backend status:', error);
      setIsWaiting(false);
      // Default to onboarding flow on error
      navigate('/instagram-onboarding', { replace: true });
    }
  };

  const startMonitoringOAuth = () => {
    // Clear any previous OAuth callback data
    localStorage.removeItem('instagram_oauth_callback');
    localStorage.removeItem('instagram_oauth_error');

    startTimeRef.current = Date.now();

    pollIntervalRef.current = setInterval(() => {
      // Check for success callback
      const callbackData = localStorage.getItem('instagram_oauth_callback');
      if (callbackData) {
        try {
          const { token, timestamp } = JSON.parse(callbackData) as { token: string; timestamp: number };

          // Verify this is a new callback (after we started monitoring)
          if (timestamp > startTimeRef.current!) {
            clearInterval(pollIntervalRef.current!);
            localStorage.removeItem('instagram_oauth_callback');
            handleOAuthSuccess(token);
            return;
          }
        } catch (parseError) {
          console.error('Error parsing callback data:', parseError);
        }
      }

      // Check for error
      const errorData = localStorage.getItem('instagram_oauth_error');
      if (errorData) {
        clearInterval(pollIntervalRef.current!);
        localStorage.removeItem('instagram_oauth_error');
        setIsWaiting(false);
        setError(errorData);
        toast.error(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
        return;
      }

      // Timeout after 5 minutes
      if (Date.now() - startTimeRef.current! > 300000) {
        clearInterval(pollIntervalRef.current!);
        setIsWaiting(false);
        setError(t('oauth.timeoutError', 'OAuth timeout. Please try again.'));
      }
    }, 500); // Poll every 500ms
  };

  const handleContinue = () => {
    if (!authUrl) return;

    setError('');
    setIsWaiting(true);

    // Start monitoring localStorage BEFORE opening the window
    startMonitoringOAuth();

    // User-initiated window.open bypasses popup blocker
    // Use _blank to open in new tab (works better in in-app browsers)
    window.open(authUrl, '_blank');
  };

  const handleCancel = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    navigate(returnPath || '/');
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Instagram sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />

        <Typography variant="h4" gutterBottom>
          {t('oauth.redirectTitle', 'Connect to Instagram')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {isWaiting
            ? t('oauth.waitingDesc', 'Complete the Instagram login in the other window, then return here.')
            : t('oauth.redirectDesc', 'Click the button below to continue with Instagram authentication.')
          }
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isWaiting ? (
          <Box sx={{ mb: 3 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Alert severity="info">
              {t('oauth.waitingInfo', 'Waiting for Instagram authentication to complete...')}
            </Alert>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('oauth.popupInfo', 'A new window will open for Instagram login. Please allow popups for this site if prompted.')}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={isWaiting ? <CircularProgress size={20} color="inherit" /> : <Instagram />}
          onClick={handleContinue}
          disabled={!authUrl || isWaiting}
          sx={{ mt: 2 }}
        >
          {isWaiting
            ? t('oauth.waitingButton', 'Waiting...')
            : t('oauth.continueButton', 'Continue to Instagram')
          }
        </Button>

        <Button
          onClick={handleCancel}
          sx={{ mt: 2, display: 'block', mx: 'auto' }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
      </Paper>
    </Container>
  );
};

export default OAuthRedirectPage;
